import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import logo from '../assets/logo-narayan.png';

/* FadeIn — entrance animation (fade + rise) with optional stagger delay.
   CSS-driven equivalent of the app's Animated FadeInView. */
export function FadeIn({ children, delay = 0, from = 18, style, className = '', ...rest }) {
  return (
    <div
      className={`fade-in ${className}`}
      style={{ animationDelay: `${delay}ms`, '--rise-from': `${from}px`, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Button({ title, children, onClick, variant = 'primary', disabled, style, className = '', type = 'button', ...rest }) {
  return (
    <button
      type={type}
      className={`btn btn--${variant} ${className}`}
      onClick={onClick}
      disabled={disabled}
      style={style}
      {...rest}
    >
      {title ?? children}
    </button>
  );
}

export function Card({ children, style, className = '', onClick, ...rest }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`card ${className}`}
      style={{ cursor: clickable ? 'pointer' : undefined, ...style }}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Badge({ label, tone = 'brand', style }) {
  return <span className={`badge badge--${tone}`} style={style}>{label}</span>;
}

/* Brand logo inside a white pill, gently floating — same treatment as the app. */
export function Logo({ size = 'md', animated = true, style }) {
  const height = size === 'lg' ? 56 : size === 'sm' ? 30 : 42;
  return (
    <div
      className={animated ? 'float' : ''}
      style={{
        background: '#fff',
        padding: '11px 18px',
        borderRadius: 'var(--r-pill)',
        boxShadow: '0 10px 18px rgba(20,22,40,0.12)',
        display: 'inline-flex',
        ...style,
      }}
    >
      <img src={logo} alt="Narayan Home Facility Services" style={{ height, width: 'auto' }} />
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="loading-center">
      <div className="spinner" />
      {label ? <div className="muted" style={{ fontSize: 13 }}>{label}</div> : null}
    </div>
  );
}

/* Labelled text input. */
export function Field({ label, error, hint, as = 'input', className = '', ...props }) {
  const Tag = as;
  return (
    <div className="field">
      {label ? <label className="field__label">{label}</label> : null}
      <Tag className={`input ${error ? 'input--error' : ''} ${className}`} {...props} />
      {error ? <div className="field__error">{error}</div> : null}
      {hint && !error ? <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>{hint}</div> : null}
    </div>
  );
}

/* Read-only value styled like a field (Become-a-Partner form). */
export function ReadonlyField({ label, value }) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <div className="input" style={{ background: 'var(--bg)', fontWeight: 700, color: 'var(--muted)' }}>{value}</div>
    </div>
  );
}

/* Locks background scrolling while an overlay is open. */
function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

/* Closes on Escape. */
function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

/* Overlays render into document.body. Anything rendered inside the screen tree
   would sit in an ancestor stacking context and could be painted over by the
   fixed tab bar, no matter how high its own z-index is. */
export function Portal({ children }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

/* Bottom sheet — the app's slide-up transparent Modal. */
export function Sheet({ open, onClose, title, children }) {
  useScrollLock(open);
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <Portal>
    <div className="modal-backdrop modal-backdrop--bottom" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet__handle" />
        {title ? (
          <div className="sheet__header">
            <div className="sheet__title">{title}</div>
            <button className="muted" onClick={onClose} aria-label="Close" style={{ fontSize: 20, padding: 4 }}>✕</button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
    </Portal>
  );
}

/* Full-page overlay — the app's presentationStyle="fullScreen" Modal.
   `variant="panel"` keeps that on phones but becomes a centred, backdrop-dimmed
   dialog from 900px up. */
export function FullScreen({ open, onClose, children, variant }) {
  useScrollLock(open);
  useEscape(open, onClose);
  if (!open) return null;
  const isPanel = variant === 'panel';
  return (
    <Portal>
      <div
        className={`fullscreen ${isPanel ? 'fullscreen--panel' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={isPanel ? (e) => { if (e.target === e.currentTarget) onClose?.(); } : undefined}
      >
        <div className="fullscreen__inner">{children}</div>
      </div>
    </Portal>
  );
}

/* Red sub-page header with a back/close control. */
export function TopBar({ title, onClose, closeIcon = '✕', action }) {
  return (
    <div className="topbar">
      {onClose ? (
        <button className="topbar__btn" onClick={onClose} aria-label="Close">{closeIcon}</button>
      ) : <span style={{ width: 34 }} />}
      <div className="topbar__title">{title}</div>
      {action || <span style={{ width: 34 }} />}
    </div>
  );
}

/* Centred empty state. */
export function Empty({ icon = '📭', title, sub }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      {title ? <div className="empty__title">{title}</div> : null}
      {sub ? <div className="empty__sub">{sub}</div> : null}
    </div>
  );
}

/* Pull-to-refresh isn't a web idiom; a visible Refresh control replaces it. */
export function RefreshButton({ onClick, busy }) {
  return (
    <button className="topbar__action" onClick={onClick} disabled={busy} style={{ opacity: busy ? 0.5 : 1 }}>
      {busy ? 'Refreshing…' : '↻ Refresh'}
    </button>
  );
}

/* Mesh-gradient background (login / partner screens). */
export function GradientBackground({ children }) {
  const blobs = [
    { background: 'var(--primary)', width: 460, height: 460, top: -140, left: -80, opacity: 0.9 },
    { background: 'var(--primary-light)', width: 340, height: 340, top: -60, right: -110, opacity: 0.75 },
    { background: 'var(--accent)', width: 320, height: 320, bottom: -120, left: -90, opacity: 0.28 },
    { background: '#ffffff', width: 260, height: 260, bottom: 40, right: -70, opacity: 0.06 },
    { background: 'var(--primary-dark)', width: 300, height: 300, bottom: -140, right: 40, opacity: 0.6 },
  ];
  return (
    <div className="gradient-bg">
      {blobs.map((b, i) => <div key={i} className="gradient-bg__blob" style={b} />)}
      <div className="gradient-bg__content">{children}</div>
    </div>
  );
}

/* Runs `fn` once on mount — keeps effect deps tidy in the screens. */
export function useMount(fn) {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/* Reactive media query — lets a screen change structure (not just styling)
   between the phone layout and the desktop layout. */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(m.matches);
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/* The breakpoint where the bottom tab bar gives way to the sidebar. */
export const useIsDesktop = () => useMediaQuery('(min-width: 900px)');
